/* 
██╗██╗░░░██╗  ██████╗░██╗░██████╗░██╗████████╗░█████╗░██╗░░░░░
██║██║░░░██║  ██╔══██╗██║██╔════╝░██║╚══██╔══╝██╔══██╗██║░░░░░
██║██║░░░██║  ██║░░██║██║██║░░██╗░██║░░░██║░░░███████║██║░░░░░
██║██║░░░██║  ██║░░██║██║██║░░╚██╗██║░░░██║░░░██╔══██║██║░░░░░
██║╚██████╔╝  ██████╔╝██║╚██████╔╝██║░░░██║░░░██║░░██║███████╗
╚═╝░╚═════╝░  ╚═════╝░╚═╝░╚═════╝░╚═╝░░░╚═╝░░░╚═╝░░╚═╝╚══════╝
╔════════════════════════════════════════════════════════════╗
║              EQUIPO DE DESARROLLO DE SOFTWARE              ║
║                   MODULO DESARROLLADO POR                  ║
╟┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄╢
║ Victor Manuel Calle Londoño                                ║
║ victor.calle@est.iudigital.edu.co                          ║
╚════════════════════════════════════════════════════════════╝
*/

/**
 * Controlador principal para la gestión de fuentes de datos.
 *
 * Este controlador maneja toda la lógica relacionada con la creación, edición, eliminación
 * y listado de fuentes de datos en el sistema de carnetización. Gestiona diferentes tipos
 * de fuentes (CSV, JSON, EXCEL, MySQL, PostgreSQL) y coordina la comunicación entre los
 * componentes de vista y los servicios del backend.
 *
 * Funcionalidades principales:
 * - Gestión del ciclo de vida de fuentes de datos (CRUD)
 * - Validación de campos del formulario en tiempo real
 * - Manejo de carga de archivos (CSV, JSON, EXCEL)
 * - Configuración de conexiones a bases de datos
 * - Soporte para carga de imagen strip PKPass (PNG, máx 2MB)
 * - Filtrado y paginación de fuentes de datos
 * - Manejo de estados y alertas del sistema
 *
 * @module DataSourceController
 * @author Victor Manuel Calle Londoño <victor.calle@est.iudigital.edu.co>
 * @date [Fecha de creación: 2023-10-18]
 * @updated [Fecha de última modificación: 2025-10-21]
 * @updatedBy eulicer.zapata@iudigital.edu.co <eulicer.zapata@iudigital.edu.co>
 */
import DataTable from "examples/Tables/DataTable";
import viewSource from "assets/images/ver_fuente.svg";
import editSource from "assets/images/edit_fuente.svg";
import deleteSource from "assets/images/delete_fuente.svg";
import closeImg from "assets/images/close_X.svg";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";
import MDInput from "components/MDInput";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import {
  createSource,
  getSourceTypes,
  getDataSources,
  updateSource,
  createSourceDB,
  getStripImage,
} from "../services/dataSourceService";
import ListDataSource from "../modules/ListDataSource";
import { SourceDelete } from "../modules/SourceDelete";
import { SourceNew } from "../modules/SourceNew";
import { SourceEdit } from "../modules/SourceEdit";
import { getCardTypesWithoutPagination } from "layouts/card_type/services/cardTypeService";
import Spinner from "components/MDSpinner";
import MDAlert from "components/Alert";
import { useMaterialUIController } from "../../../context/index";
import { DataSourceFileController } from "layouts/sources_data_file/controllers/DataSourceFileController";
import FilterController from "components/filter/controller/FilterController";
import usePermission from "hooks/usePermission";
import { useNavigate } from "react-router-dom";

export default function DataSourceController() {
  const navigate = useNavigate();
  const [controller, dispatch] = useMaterialUIController();
  const [openEdit, setOpenEdit] = useState(false);
  const [openNew, setOpenNew] = useState(false);
  const [sourceTypes, setSourceTypes] = useState([]);
  const [carnetTypes, setCarnetTypes] = useState([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isAlert, setIsAlert] = useState(false);
  const [isAlertSuccess, setIsAlertSuccess] = useState(false);
  const [infoSuccess, setInfoSuccess] = useState();
  const [spinner, setSpinner] = useState(false);
  const [errorAlert, setErrorAlert] = useState("");
  const [dataSources, setDataSources] = useState([]);
  const [recoveredData, setRecoveredData] = useState({});
  const [editedData, setEditedData] = useState({});
  const [newData, setNewData] = useState({});
  const [newDataDB, setNewDataDB] = useState({});
  const [infoDelete, setInfoDelete] = useState({});
  const [sourceList, setSourceList] = useState();
  const [defaultData, setDefaultData] = useState({});
  const [isFormModified, setIsFormModified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [typeAlert, setTypeAlert] = useState("");
  const [message, setMessage] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [totalData, setTotalData] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesStart, setEntriesStart] = useState(0);
  const [entriesEnd, setEntriesEnd] = useState(0);

  // Estados para manejar la imagen strip en edición
  const [stripImageUrl, setStripImageUrl] = useState(null); // URL de la imagen actual (base64)
  const [stripImageFile, setStripImageFile] = useState(null); // Nueva imagen a subir

  // Estados para validación de caracteres especiales en edición
  const [fieldErrors, setFieldErrors] = useState({
    source_name: null,
    source_details: null,
    location: null,
    address: null,
  });

  // Estados para validación de caracteres especiales en creación
  const [fieldErrorsNew, setFieldErrorsNew] = useState({
    source_name: null,
    source_details: null,
    location: null,
    address: null,
  });

  // Patrones de validación para cada campo
  const fieldPatterns = {
    source_name: /^[A-Za-zÀ-ÖØ-öø-ÿ0-9\s\-_.,()&ñÑ]*$/,
    source_details: /^[A-Za-zÀ-ÖØ-öø-ÿ0-9\s\-_.,()&ñÑ]*$/,
    location: /^[A-Za-zÀ-ÖØ-öø-ÿ0-9\s,.\-/#()º°:;'"&ñÑ]*$/,
    address: /^[A-Za-zÀ-ÖØ-öø-ÿ0-9\s,.\-/#()º°:;'"&ñÑ]*$/,
  };

  const filterControllerRef = useRef();
  const [error, setError] = useState();

  //Constantes de filtro
  const dataFilter = useState([
    {
      field: "NOMBRE",
      value: "source_name",
    },
    {
      field: "TIPO DE FUENTE",
      value: "source_type_name",
    },
    {
      field: "RESPONSABLE",
      value: "user_email",
    },
    {
      field: "FECHA DE CREACIÓN",
      value: "created_at",
    },
  ]);
  const dataTable = "data_sources"; //Tabla indicada
  //Esperar respuesta de la función getSources y validar respuesta para listar las fuentes de datos o mostrar error
  const requestDataSources = async (pageIndex) => {
    try {
      const result = await getDataSources(pageIndex);
      if (result.status !== false) {
        const { data, last_page, total, from, to } = result;
        //if (data.length !== 0) {
        //}
        await setDataSources(data);
        await setTotalPages(last_page);
        await setTotalData(total);
        await setEntriesStart(from);
        await setEntriesEnd(to);
        await setLoading2(false);
        await setLoading(false);
      } else {
        await setLoading2(false);
        await setLoading(false);
        handleAlert("fail", result.message);
      }
    } catch (error) {
      await setLoading2(false);
      await setLoading(false);
      handleAlert("fail", error.message);
    }
  };

  // Esperar respuesta de la función getSourceTypes y validar respuesta para listar los tipos de fuentes disponibles o mostrar error
  const requestSourceTypes = async (pageIndex) => {
    const result = await getSourceTypes(pageIndex);

    if (result.status !== false) {
      const { payload } = result;
      await setSourceTypes(payload);
    } else {
      handleAlert("fail", "No se pudieron cargar los tipos de fuentes");
    }
  };

  //Esperar respuesta de la función getCardTypes y validar respuesta para listar los tipos de carnet o mostrar error
  const requestCarnetTypes = async (pageIndex) => {
    const result = await getCardTypesWithoutPagination();

    if (result.status !== false) {
      const { payload } = result;
      await setCarnetTypes(payload);
    } else {
      handleAlert("fail", "No se pudieron cargar los tipos de carnet");
    }
  };

  // Esperar respuesta de la función createSource y validar respuesta para conocer si se inserto correctamente la fuente de datos o mostrar error
  const requestSourceNew = async (data) => {
    try {
      const result = await createSource(data);
      if (result.status !== false) {
        setInfoSuccess(result.payload);
        handleAlert("success", "Se uso correctamente la información de la fuente de datos");
        setLoading2(true);
        // await requestDataSources(1);
        // Función para llamar a handleClickSubmit desde el componente padre y actualizar la tabla con los filtros actuales
        callHandleClickSubmit();
        await setLoading2(false);
        handleCloseNew();
        //const { message, last_page, total, from, to } = result;
      } else {
        if (result.message === "Archivo no encontrado") {
          await setLoading2(false);
          handleAlert("fail", "Archivo no encontrado");
        } else if (result.message === "type_card not found") {
          await setLoading2(false);
          handleAlert("fail", "El tipo de carnet no existe");
        } else {
          await setLoading2(false);
          handleAlert("fail", result.message);
        }
        //setError("Error al consultar al servidor");
      }
    } catch (error) {
      await setLoading2(false);
      handleAlert("fail", error.message || "Fallo al procesar el archivo");
    }
  };

  // Esperar respuesta de la función createSourceDB y validar respuesta para conocer si se inserto correctamente la fuente de datos o mostrar error
  const requestSourceNewConexionDB = async (data) => {
    try {
      const result = await createSourceDB(data);
      if (result.status !== false) {
        setColumData(result.payload);
        handleAlert("success", "Se estableció conexión correctamente con la base de datos");
        //const { message, last_page, total, from, to } = result;
      } else {
        setIsAlert(true);
        if (result.message === "Archivo CSV no encontrado") {
          handleAlert("fail", "Archivo CSV no encontrado");
        } else if (result.message === "type_card not found") {
          handleAlert("fail", "El tipo de carnet no existe");
        } else {
          handleAlert("fail", result.message || "Fallo al procesar la base de datos elegida");
        }
        //setError("Error al consultar al servidor");
      }
    } catch (error) {
      handleAlert("fail", error.message || "Fallo al procesar la base de datos elegida");
    }
  };

  // Realiza una solicitud para crear una nueva fuente de datos de base de datos utilizando la información proporcionada.
  const requestSourceNewDB = async (data) => {
    try {
      const result = await createSourceDB(data);
      if (result.status !== false) {
        handleAlert("success", "Se uso correctamente la información de la base de datos");
        //const { message, last_page, total, from, to } = result;
      } else {
        setIsAlert(true);
        if (result.message === "Archivo CSV no encontrado") {
          handleAlert("fail", "Archivo CSV no encontrado");
        } else if (result.message === "type_card not found") {
          handleAlert("fail", "El tipo de carnet no existe");
        } else {
          handleAlert(
            "fail",
            result.message || "No se pudo usar la información de la base de datos"
          );
        }
        //setError("Error al consultar al servidor");
      }
    } catch (error) {
      handleAlert("fail", error.message || "No se pudo usar la información de la base de datos");
    }
  };

  // Peticion de las fuentes de datos disponibles
  const requestSourceEdit = async (id, data) => {
    try {
      const result = await updateSource(id, data);
      if (result.status !== false) {
        handleAlert("success", "Se actualizó correctamente la fuente de datos");
        setLoading(true);
        // await requestDataSources(1);
        // Función para llamar a handleClickSubmit desde el componente padre y actualizar la tabla con los filtros actuales
        callHandleClickSubmit();
        await setLoading(false);
        handleCloseEdit();
        //const { message, last_page, total, from, to } = result;
      } else {
        handleAlert("fail", result.message || "No se pudo actualizar la fuente de datos");
        setLoading(false);
        //setError("Error al consultar al servidor");
      }
    } catch (error) {
      await setLoading(false);
      handleAlert("fail", error.message || "No se pudo actualizar la fuente de datos");
    }
  };

  // Maneja las alertas de exito fallo
  const handleAlert = (typeAlert, message) => {
    setTypeAlert(typeAlert);
    setMessage(message);
    setIsAlert(true);
  };

  //Maneja la paginacion de la tabla
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Valida un string con formato "HH:MM:SS" y extrae la hora, retornando 0 en caso de error
  function getHour(timeStr) {
    // Verificar si el valor es un string
    if (typeof timeStr !== "string") {
      console.error("Valor de tiempo no es un string:", timeStr);
      return 0;
    }
    // Separar la cadena en partes
    const parts = timeStr.split(":");
    // Verificar si la cadena tiene el formato correcto
    if (parts.length !== 3) {
      console.error("Formato de hora inválido:", timeStr);
      return 0;
    }
    // Extraer la hora
    const hour = parseInt(parts[0], 10);
    // Verificar si la hora es un número
    if (isNaN(hour)) {
      console.error("No se pudo convertir la hora:", parts[0]);
      return 0;
    }
    return hour;
  }

  /**
   * Valida un string con formato "HH:MM:SS" y extrae el minuto, retornando 0 en caso de error
   *
   * @param {string} timeStr - String con formato "HH:MM:SS"
   * @returns {number} - El valor del minuto como número
   */
  function getMinute(timeStr) {
    // Verificar si el valor es un string
    if (typeof timeStr !== "string") {
      console.error("Valor de tiempo no es un string:", timeStr);
      return 0;
    }
    // Separar la cadena en partes
    const parts = timeStr.split(":");
    // Verificar si la cadena tiene el formato correcto
    if (parts.length !== 3) {
      console.error("Formato de hora inválido:", timeStr);
      return 0;
    }
    // Extraer el minuto (segunda parte)
    const minute = parseInt(parts[1], 10);
    // Verificar si el minuto es un número
    if (isNaN(minute)) {
      console.error("No se pudo convertir el minuto:", parts[1]);
      return 0;
    }
    return minute;
  }

  /**
   * Abre el modal para editar una fuente de datos existente.
   *
   * Inicializa el formulario con los datos actuales de la fuente, incluyendo:
   * - Información general (nombre, descripción)
   * - Ubicación y dirección (si es Invitado/Visitante)
   * - Horarios de check-in y check-out
   * - Imagen strip si existe (descarga y muestra preview)
   *
   * Los campos de ubicación y dirección solo son obligatorios para carnets
   * tipo Invitado o Visitante, según las reglas del backend.
   *
   * @param {Event} event - El evento del botón que abre el modal.
   *                        Debe contener dataset con todos los campos necesarios.
   * @returns {Promise<void>}
   *
   */
  const handleOpenEdit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setOpenEdit(true);
    setIsDisabled(event.currentTarget.dataset.id === "view" ? true : false);
    const { dataset } = event.currentTarget;

    const timeStringIn = dataset.checkInTime; // Ejemplo: "01:00:00" o "00:00:00"
    const timeStringOut = dataset.checkOutTime; // Ejemplo: "01:00:00" o "00:00:00"
    const hourNumberIn = getHour(timeStringIn);
    const minuteNumberIn = getMinute(timeStringIn);
    const hourNumberOut = getHour(timeStringOut);
    const minuteNumberOut = getMinute(timeStringOut);

    const data = {
      id: dataset.identifier,
      source_name: dataset.sourceName || "",
      source_details: dataset.sourceDescription || "",
      location: dataset.location || "",
      address: dataset.address || "",
      check_in_time: hourNumberIn,
      check_in_minute: minuteNumberIn,
      check_out_time: hourNumberOut,
      check_out_minute: minuteNumberOut,
      perfil: dataset.cardTypeName, // Tipo de carnet para validaciones condicionales
    };

    setRecoveredData(data);
    setEditedData(data);

    // Reiniciar estados de imagen strip
    setStripImageUrl(null);
    setStripImageFile(null);

    // Cargar la imagen strip actual desde el backend si existe
    try {
      const stripImageData = await getStripImage(dataset.identifier);
      if (stripImageData && stripImageData.has_image && stripImageData.image) {
        setStripImageUrl(stripImageData.image); // Ya viene en formato data URI (base64)
      }
    } catch (error) {
      console.error("Error al cargar la imagen strip:", error);
    }

    setLoading(false);
  };

  /**
   * Abre el modal para crear una nueva fuente de datos.
   *
   * Inicializa el estado del formulario con valores por defecto, incluyendo
   * el campo opcional strip_image para la imagen de franja del PKPass.
   * Carga los tipos de fuentes y tipos de carnet disponibles desde el backend.
   *
   * @param {Event} event - El evento del botón que abre el modal.
   * @returns {Promise<void>}
   */
  const handleOpenNew = async (event) => {
    setLoading(true);
    event.preventDefault();
    setIsDisabled(false);
    resetFieldValid();

    const data = {
      name: "",
      source_name: "",
      source_description: "",
      source: "",
      perfil: "",
      created: "",
      expires_in: "",
      username: controller.user_id,
      check_in_time: 1,
      check_in_minute: 0,
      check_out_time: 0,
      check_out_minute: 0,
      strip_image: null, // Imagen strip del PKPass (PNG, máx 2MB) - Obligatorio para Invitado/Visitante
    };

    setDefaultData(data);
    await requestSourceTypes(1);
    await requestCarnetTypes(1);
    setNewData(data);
    setOpenNew(true);
    setLoading(false);
  };

  // Logica para la actualización de archivos de fuentes de datos ----->
  const [openDataSourcesFileBoard, setOpenDataSourcesFileBoard] = useState(false);
  const [idDataSource, setIdDataSource] = useState("");
  const [nameDataSourceFile, setNameDataSourceFile] = useState("");

  //Abre el modal donde se listan los carnets de un usuario en especifico
  const handleOpenDataSourcesFileBoard = (event) => {
    event.preventDefault();
    setOpenDataSourcesFileBoard(true);
    const { dataset } = event.currentTarget;
    setIdDataSource(dataset.identifier);
    setNameDataSourceFile(dataset.name);
  };

  const handleCloseDataSourcesFileController = () => {
    setOpenDataSourcesFileBoard(false);
  };

  // Maneja la apertura de la página de formulario de evento
  const handleOpenEventForm = (event) => {
    event.preventDefault();
    const { dataset } = event.currentTarget;
    const dataSourceId = dataset.datasourceId;

    // Navegar a la nueva página independiente
    navigate(`/event-form/${dataSourceId}`);
  };

  // <------
  // Abre un modal de confirmación para eliminar el elemento seleccionado.
  const handleClickDelete = (event) => {
    event.preventDefault();

    const { dataset } = event.currentTarget;
    const data = {
      id: dataset.identificador,
      nombre: dataset.fuente,
      url: dataset.url,
      estado: dataset.estado,
      usuarios: dataset.usuarios,
      fecha_de_generacion: dataset.fechac,
      perfil: dataset.perfil,
      programa: dataset.programa,
    };
    setInfoDelete(data);
    setOpenDelete(true);
  };

  const handleCloseEdit = () => setOpenEdit(false);
  const handleCloseNew = () => setOpenNew(false);
  const handleCloseDelete = () => setOpenDelete(false);
  const header = [{}];

  //Obtiene el valor de un campo específico del conjunto de datos editados.
  const getFieldValue = (fieldName) => {
    return editedData[fieldName];
  };

  // Obtiene el valor de un campo específico del conjunto de datos nuevos.
  const getFieldValueNew = (fieldName) => {
    return newData[fieldName];
  };

  //Actualiza los datos editados con el nuevo valor del campo especificado.
  const handleFieldChange = (fieldName, value) => {
    // Validar caracteres especiales para campos específicos
    const pattern = fieldPatterns[fieldName];
    let errorMessage = null;

    // Campos obligatorios
    const requiredFields = ["source_name"];
    const isGuestCarnet = editedData.perfil === "Invitado" || editedData.perfil === "Visitante";
    if (isGuestCarnet) {
      requiredFields.push("location", "address");
    }

    // Validar si el campo está vacío y es obligatorio
    if (requiredFields.includes(fieldName) && (!value || value.trim() === "")) {
      errorMessage = `Este campo no puede estar vacío`;
    }
    // Validar caracteres especiales
    else if (pattern && !pattern.test(value)) {
      errorMessage = `El campo contiene caracteres no permitidos`;
    }

    setFieldErrors({
      ...fieldErrors,
      [fieldName]: errorMessage,
    });

    setEditedData({
      ...editedData,
      [fieldName]: value,
    });
  };

  /**
   * Maneja la validación y cambio de imagen strip para formularios de creación y edición.
   *
   * Valida que el archivo cumpla con los requisitos de Apple PKPass:
   * - Formato PNG únicamente
   * - Tamaño máximo de 2MB
   * - Dimensiones exactas: 423 × 112 píxeles
   *
   * Puede usarse en modo creación (con resetInput) o modo edición (sin resetInput).
   * En modo creación, actualiza el campo usando handleFieldChangeNew.
   * En modo edición, actualiza el estado stripImageFile directamente.
   *
   * @param {File|null} file - El archivo de imagen seleccionado por el usuario, o null para limpiar.
   * @param {Function} [resetInput] - Función opcional para limpiar el input file (usado en creación).
   * @returns {void}
   *
   */
  const handleStripImageChange = (file, resetInput) => {
    const isCreationMode = typeof resetInput === "function";

    if (!file) {
      if (isCreationMode) {
        handleFieldChangeNew("strip_image", null);
      } else {
        setStripImageFile(null);
      }
      return;
    }

    // Validar tipo PNG (requerido por Apple PKPass specifications)
    if (file.type !== "image/png") {
      handleAlert("fail", "La imagen debe ser formato PNG");
      if (resetInput) resetInput();
      return;
    }

    // Validar tamaño máximo 2MB (2,097,152 bytes)
    if (file.size > 2097152) {
      handleAlert("fail", "La imagen no debe superar 2MB");
      if (resetInput) resetInput();
      return;
    }

    // Validar dimensiones exactas: 423 × 112 píxeles
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl); // Liberar memoria

      // Permitir un margen de ±10 píxeles en las dimensiones
      const targetWidth = 423;
      const targetHeight = 112;
      const margin = 10;

      const widthInRange = img.width >= targetWidth - margin && img.width <= targetWidth + margin;
      const heightInRange =
        img.height >= targetHeight - margin && img.height <= targetHeight + margin;

      if (!widthInRange || !heightInRange) {
        handleAlert(
          "fail",
          `La imagen debe tener dimensiones de 423 × 112 píxeles . Dimensiones actuales: ${img.width} × ${img.height} píxeles`
        );
        if (resetInput) resetInput();
        return;
      }

      // Si pasa todas las validaciones, actualizar según el modo
      if (isCreationMode) {
        handleFieldChangeNew("strip_image", file);
      } else {
        setStripImageFile(file);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      handleAlert("fail", "Error al cargar la imagen. Por favor, intente con otro archivo");
      if (resetInput) resetInput();
    };

    img.src = objectUrl;
  };

  /**
   * Maneja el cambio de valor de un campo en los datos de la nueva fuente.
   *
   * Actualiza el estado de los datos nuevos según el campo y el valor especificados.
   * Realiza validaciones específicas según el tipo de campo y gestiona campos
   * condicionales dependiendo del tipo de fuente seleccionada.
   *
   * @param {string} fieldName - El nombre del campo que cambió.
   * @param {*} value - El nuevo valor del campo (puede ser string, File, etc.).
   * @returns {void}
   */
  const handleFieldChangeNew = (fieldName, value) => {
    handleChangeValid(fieldName, value);

    // Validar caracteres especiales para campos específicos
    const pattern = fieldPatterns[fieldName];
    let errorMessage = null;

    // Determinar si el campo es obligatorio (requiere validación de vacío)
    const isRequiredField =
      fieldName === "source_name" ||
      (fieldName === "location" &&
        (newData.perfil === "Invitado" || newData.perfil === "Visitante")) ||
      (fieldName === "address" &&
        (newData.perfil === "Invitado" || newData.perfil === "Visitante"));

    // source_description es opcional - nunca mostrar error
    if (fieldName === "source_description") {
      errorMessage = null;
    }
    // Primero validar si el campo está vacío (para campos obligatorios)
    else if (isRequiredField && typeof value === "string" && value.trim() === "") {
      errorMessage = "Este campo no puede estar vacío";
    }
    // Luego validar caracteres (solo si el campo no está vacío o si es opcional)
    else if (pattern && typeof value === "string" && value.trim() !== "" && !pattern.test(value)) {
      errorMessage = `El campo contiene caracteres no permitidos`;
    }

    setFieldErrorsNew({
      ...fieldErrorsNew,
      [fieldName]: errorMessage,
    });

    // Si cambia el tipo de carnet, validar nuevamente strip_image
    if (fieldName === "perfil") {
      const isGuestOrVisitor = value === "Invitado" || value === "Visitante";
      // Si cambió a un tipo que NO requiere imagen y no hay imagen, es válido
      // Si cambió a un tipo que SÍ requiere imagen y no hay imagen, es inválido
      if (!newData.strip_image) {
        setIsFieldValid((prevValidity) => ({
          ...prevValidity,
          strip_image: !isGuestOrVisitor,
        }));
      }
    }

    if (fieldName === "name" && (value === "MySQL" || value === "PostgreSQL")) {
      newData["port"] = "";
      newData["db_host"] = "";
      newData["nameDB"] = "";
      newData["nameTable"] = "";
      newData["db_username"] = "";
      newData["db_password"] = "";
      newData["colums"] = "";
      setColumData([]);
      setSelectedValues({});
      delete newData["source"];
    } else if (fieldName === "name" && (value !== "PostgreSQL" || value !== "MySQL")) {
      delete newData["port"];
      delete newData["db_host"];
      delete newData["nameDB"];
      delete newData["nameTable"];
      delete newData["db_username"];
      delete newData["db_password"];
      delete newData["colums"];
      newData["source"] = "";
    }
    setNewData({
      ...newData,
      [fieldName]: value,
    });
  };

  useEffect(() => {
    // Comprueba si algún campo ha cambiado desde los valores iniciales
    const hasFormChanged = Object.keys(editedData).some(
      (fieldName) => editedData[fieldName] !== recoveredData[fieldName]
    );

    // Validar solo campos obligatorios (source_name es obligatorio)
    const isSourceNameEmpty = !editedData.source_name || editedData.source_name.trim() === "";

    // Para Invitado/Visitante, validar que location y address no estén vacíos si están presentes
    const requiresGuestFields =
      editedData.perfil === "Invitado" || editedData.perfil === "Visitante";

    let isRequiredFieldEmpty = isSourceNameEmpty;

    if (requiresGuestFields && hasFormChanged) {
      const isLocationEmpty =
        editedData.location !== undefined &&
        (!editedData.location || editedData.location.trim() === "");
      const isAddressEmpty =
        editedData.address !== undefined &&
        (!editedData.address || editedData.address.trim() === "");

      isRequiredFieldEmpty = isSourceNameEmpty || isLocationEmpty || isAddressEmpty;
    }

    // Verificar si hay errores de validación en los campos
    const hasFieldErrors = Object.values(fieldErrors).some((error) => error !== null);

    // También considerar si hay una nueva imagen strip seleccionada
    const hasImageChanged = stripImageFile !== null;

    setIsFormModified(
      (!isRequiredFieldEmpty && hasFormChanged && !hasFieldErrors) || hasImageChanged
    );
  }, [editedData, stripImageFile, recoveredData, fieldErrors]);

  useEffect(() => {
    // Comprueba si algún campo ha cambiado desde los valores iniciales
    //const hasFormChanged = Object.keys(newData).some((fieldName) => newData[fieldName] !== "");
    //const requiredFields = ["name", "source", "perfil", "created"];
    const requiredFields = Object.keys(newData).filter(
      (clave) =>
        clave !== "db_password" &&
        clave !== "created" &&
        clave !== "expires_in" &&
        clave !== "check_in_time" &&
        clave !== "check_out_time" &&
        clave !== "check_in_minute" &&
        clave !== "check_out_minute" &&
        clave !== "source_description"
    );

    // Para Invitado y Visitante, strip_image, location y address son obligatorios
    const isGuestOrVisitor = newData.perfil === "Invitado" || newData.perfil === "Visitante";

    // Si NO es invitado/visitante, excluir strip_image, location y address de campos requeridos
    const fieldsToValidate = isGuestOrVisitor
      ? requiredFields
      : requiredFields.filter(
          (clave) => clave !== "strip_image" && clave !== "location" && clave !== "address"
        );

    const someFieldIsEmpty = fieldsToValidate.some((fieldName) => !newData[fieldName]);

    // Verificar si hay errores de validación en los campos (excluyendo source_description que es opcional)
    const hasFieldErrors = Object.entries(fieldErrorsNew)
      .filter(([key]) => key !== "source_description")
      .some(([, error]) => error !== null);

    // El formulario es válido solo si no hay campos vacíos requeridos y no hay errores
    setIsFormModified(someFieldIsEmpty || hasFieldErrors);
  }, [newData, fieldErrorsNew]);

  // useEffect(() => {
  //   requestDataSources(currentPage);
  // }, [currentPage]);

  //Convierte los valores de un objeto en cadenas de texto.
  function convertValuesToString(objeto) {
    const convertedObject = {};

    for (const clave in objeto) {
      if (objeto.hasOwnProperty(clave)) {
        convertedObject[clave] = String(objeto[clave]);
      }
    }

    return convertedObject;
  }

  /**
   * Maneja el envío del formulario de edición de una fuente de datos.
   *
   * Construye un FormData con los campos actualizados según lo permitido por el backend:
   * - source_name: Nombre de la fuente (máx. 255 caracteres)
   * - source_details: Descripción de la fuente (máx. 500 caracteres)
   * - location: Lugar del evento (máx. 25 caracteres, obligatorio para Invitado/Visitante)
   * - address: Dirección del evento (máx. 100 caracteres, obligatorio para Invitado/Visitante)
   * - check_in_time/check_out_time: Horarios de entrada/salida (0-23)
   * - strip_image: Nueva imagen PNG para PKPass (máx. 2MB) - Reemplaza la existente si se sube
   *
   * Valida campos obligatorios según el tipo de carnet antes de enviar.
   * Utiliza FormData para soportar envío de archivos.
   *
   * @param {Event} e - El evento de submit del formulario.
   * @returns {Promise<void>}
   *
   */
  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validar que source_name no esté vacío (siempre obligatorio)
      const sourceNameValue = (editedData.source_name || "").trim();
      if (!sourceNameValue || sourceNameValue.length === 0) {
        handleAlert("fail", "El campo 'Nombre de la fuente' no puede estar vacío");
        setLoading(false);
        return;
      }

      // Determinar si es tipo Invitado o Visitante
      const requiresGuestFields =
        editedData.perfil === "Invitado" || editedData.perfil === "Visitante";

      // Validar campos obligatorios para Invitado/Visitante
      if (requiresGuestFields) {
        const locationValue = (editedData.location || "").trim();
        const addressValue = (editedData.address || "").trim();

        if (!locationValue || locationValue.length === 0) {
          handleAlert("fail", "El campo 'Lugar' no puede estar vacío");
          setLoading(false);
          return;
        }

        if (!addressValue || addressValue.length === 0) {
          handleAlert("fail", "El campo 'Dirección' no puede estar vacío");
          setLoading(false);
          return;
        }

        // Validar formato con regex del backend
        const locationRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9\s,\.\-\/\#\(\)º°:\;\'\"\&]{1,25}$/;
        const addressRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9\s,\.\-\/\#\(\)º°:\;\'\"\&]{1,100}$/;

        if (!locationRegex.test(locationValue)) {
          handleAlert(
            "fail",
            "El campo 'Lugar' contiene caracteres no permitidos o supera los 25 caracteres"
          );
          setLoading(false);
          return;
        }

        if (!addressRegex.test(addressValue)) {
          handleAlert(
            "fail",
            "El campo 'Dirección' contiene caracteres no permitidos o supera los 100 caracteres"
          );
          setLoading(false);
          return;
        }
      }

      // Crear FormData para enviar datos incluyendo posible imagen
      const formData = new FormData();

      // Agregar campos generales (solo si están modificados)
      if (editedData.source_name && editedData.source_name.trim() !== "") {
        formData.append("source_name", editedData.source_name.trim());
      }

      if (editedData.source_details && editedData.source_details.trim() !== "") {
        formData.append("source_details", editedData.source_details.trim());
      }

      // Agregar campos de ubicación solo para Invitado/Visitante
      if (requiresGuestFields) {
        if (editedData.location && editedData.location.trim() !== "") {
          formData.append("location", editedData.location.trim());
        }
        if (editedData.address && editedData.address.trim() !== "") {
          formData.append("address", editedData.address.trim());
        }
      }

      // Agregar campos de horarios
      formData.append("check_in_time", editedData.check_in_time);
      formData.append("check_out_time", editedData.check_out_time);

      // Si hay una nueva imagen strip, agregarla (reemplazará la actual en el backend)
      if (stripImageFile) {
        formData.append("strip_image", stripImageFile);
      }

      await requestSourceEdit(editedData.id, formData);
    } catch (error) {
      handleAlert("fail", error.message || "Error al actualizar la fuente de datos");
    } finally {
      setLoading(false);
      handleCloseEdit();
    }
  };

  /**  /**
   * Maneja el envío del formulario de nueva fuente de datos.
   *
   * Procesa los datos del formulario según el tipo de fuente seleccionado (CSV, JSON, EXCEL,
   * MySQL o PostgreSQL). Para archivos (CSV, JSON, EXCEL), crea un FormData que incluye todos
   * los campos del formulario, incluyendo la imagen strip opcional si fue seleccionada.
   * La imagen strip se utiliza para generar la franja visual en los PKPass de Apple Wallet.
   * Valida el tipo de archivo antes de enviar y muestra alertas en caso de error.
   *
   * @param {Event} e - El evento de submit del formulario.
   * @returns {Promise<void>}
   */
  const handleSubmitNew = async (e) => {
    e.preventDefault();
    setIsAlert(false);
    setErrorAlert("");
    setLoading(true);

    if (newData.name === "CSV") {
      // Validar location y address si aplica
      if (newData.perfil === "Invitado" || newData.perfil === "Visitante") {
        const locValid = validationPatterns.location.test((newData.location || "").trim());
        const addrValid = validationPatterns.address.test((newData.address || "").trim());
        setIsFieldValid((prev) => ({ ...prev, location: locValid, address: addrValid }));
        if (!locValid || !addrValid) {
          setLoading(false);
          handleAlert(
            "fail",
            "Por favor corrige los campos Lugar del evento o Dirección antes de continuar."
          );
          return;
        }
      }
      const formData = new FormData();
      formData.append("name", newData.name);
      formData.append("source_name", newData.source_name);
      formData.append("source_description", newData.source_description);
      formData.append("source", newData.source);
      formData.append("perfil", newData.perfil);
      formData.append("created", newData.created);
      formData.append("expires_in", newData.expires_in);
      formData.append("username", newData.username);
      formData.append("check_in_time", newData.check_in_time);
      formData.append("check_in_minute", newData.check_in_minute);
      formData.append("check_out_time", newData.check_out_time);
      formData.append("check_out_minute", newData.check_out_minute);

      // Agregar campos de ubicación para Invitado y Visitante
      if (newData.perfil === "Invitado" || newData.perfil === "Visitante") {
        if (newData.location) {
          formData.append("location", newData.location);
        }
        if (newData.address) {
          formData.append("address", newData.address);
        }
      }

      // Agregar la imagen strip para PKPass (PNG, máx 2MB) - Obligatorio para Invitado/Visitante
      if (newData.strip_image) {
        formData.append("strip_image", newData.strip_image);
      }

      const file = getFieldValueNew("source").type;

      if (file === "text/csv") {
        await requestSourceNew(formData);
        setLoading(false);
      } else {
        setIsAlert(true);
        setLoading(false);
        handleAlert("fail", "Por favor, selecciona un archivo CSV");
        // El archivo no es de tipo CSV, realiza la lógica de manejo de error.
      }
    } else if (newData.name === "JSON") {
      // Validar location y address si aplica
      if (newData.perfil === "Invitado" || newData.perfil === "Visitante") {
        const locValid = validationPatterns.location.test((newData.location || "").trim());
        const addrValid = validationPatterns.address.test((newData.address || "").trim());
        setIsFieldValid((prev) => ({ ...prev, location: locValid, address: addrValid }));
        if (!locValid || !addrValid) {
          setLoading(false);
          handleAlert(
            "fail",
            "Por favor corrige los campos Lugar del evento o Dirección antes de continuar."
          );
          return;
        }
      }
      const formData = new FormData();
      formData.append("name", newData.name);
      formData.append("source_name", newData.source_name);
      formData.append("source_description", newData.source_description);
      formData.append("source", newData.source);
      formData.append("perfil", newData.perfil);
      formData.append("created", newData.created);
      formData.append("expires_in", newData.expires_in);
      formData.append("username", newData.username);
      formData.append("check_in_time", newData.check_in_time);
      formData.append("check_in_minute", newData.check_in_minute);
      formData.append("check_out_time", newData.check_out_time);
      formData.append("check_out_minute", newData.check_out_minute);

      // Agregar campos de ubicación para Invitado y Visitante
      if (newData.perfil === "Invitado" || newData.perfil === "Visitante") {
        if (newData.location) {
          formData.append("location", newData.location);
        }
        if (newData.address) {
          formData.append("address", newData.address);
        }
      }

      // Agregar la imagen strip para PKPass (PNG, máx 2MB) - Obligatorio para Invitado/Visitante
      if (newData.strip_image) {
        formData.append("strip_image", newData.strip_image);
      }

      const file = getFieldValueNew("source").type;

      if (file === "application/json") {
        // El archivo es un archivo CSV, puedes realizar acciones con él.
        let file = getFieldValueNew("source"); // Obteniendo el objeto File
        // let reader = new FileReader();

        // reader.onload = function (event) {
        //   let data = JSON.parse(event.target.result);
        //   let keys = Object.keys(data[0]);
        // };

        // reader.onerror = function (event) {
        //   handleAlert("fail", "Error al leer el archivo JSON");
        //   return;
        // };

        // reader.readAsText(file);

        await requestSourceNew(formData);
        setLoading(false);
        //handleCloseNew();
      } else {
        setIsAlert(true);
        setLoading(false);
        handleAlert("fail", "Por favor, selecciona un archivo JSON");
        // El archivo no es de tipo CSV, realiza la lógica de manejo de error.
      }
    } else if (newData.name === "EXCEL") {
      // Validar location y address si aplica
      if (newData.perfil === "Invitado" || newData.perfil === "Visitante") {
        const locValid = validationPatterns.location.test((newData.location || "").trim());
        const addrValid = validationPatterns.address.test((newData.address || "").trim());
        setIsFieldValid((prev) => ({ ...prev, location: locValid, address: addrValid }));
        if (!locValid || !addrValid) {
          setLoading(false);
          handleAlert(
            "fail",
            "Por favor corrige los campos Lugar del evento o Dirección antes de continuar."
          );
          return;
        }
      }
      const formData = new FormData();
      formData.append("name", newData.name);
      formData.append("source_name", newData.source_name);
      formData.append("source_description", newData.source_description);
      formData.append("source", newData.source);
      formData.append("perfil", newData.perfil);
      formData.append("created", newData.created);
      formData.append("expires_in", newData.expires_in);
      formData.append("username", newData.username);
      formData.append("check_in_time", newData.check_in_time);
      formData.append("check_in_minute", newData.check_in_minute);
      formData.append("check_out_time", newData.check_out_time);
      formData.append("check_out_minute", newData.check_out_minute);

      // Agregar campos de ubicación para Invitado y Visitante
      if (newData.perfil === "Invitado" || newData.perfil === "Visitante") {
        if (newData.location) {
          formData.append("location", newData.location);
        }
        if (newData.address) {
          formData.append("address", newData.address);
        }
      }

      // Agregar la imagen strip para PKPass (PNG, máx 2MB) - Obligatorio para Invitado/Visitante
      if (newData.strip_image) {
        formData.append("strip_image", newData.strip_image);
      }

      const file = getFieldValueNew("source").type;

      if (
        file === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file === "application/vnd.ms-excel"
      ) {
        await requestSourceNew(formData);
        setLoading(false);
        handleCloseNew();
      } else {
        setIsAlert(true);
        setLoading(false);
        handleAlert("fail", "Por favor, selecciona un archivo EXCEL");
        // El archivo no es de tipo CSV, realiza la lógica de manejo de error.
      }
    } else if (newData.name === "MySQL") {
      await requestSourceNewDB(newData);
      setLoading(false);
      handleCloseNew();
    } else if (newData.name === "PostgreSQL") {
      await requestSourceNewDB(newData);
      setLoading(false);
      handleCloseNew();
    }

    //const convertedObject = convertValuesToString(newData);
    // Realiza la acción de enviar el formulario aquí
    //requestSourceNew(newData);
    setLoading(false);
  };

  const handleSubmitDelete = (e) => {
    e.preventDefault();
    handleCloseDelete();
  };

  const handleCloseAlertSuccess = () => {
    setIsAlertSuccess(false);
  };

  // Fuente de datos por base de datos -->
  const [columTableNative, setColumTableNative] = useState("");
  const [columData, setColumData] = useState([]);
  const [statusConexion, setStatusConexion] = useState(false);

  const [selectedValues, setSelectedValues] = useState({});

  const handleChange = (event, index) => {
    setNewData({
      ...newData,
      ["colums"]: { ...selectedValues, [index]: event.target.value },
    });
    setSelectedValues({ ...selectedValues, [index]: event.target.value });
  };

  const staticValues = ["Nombre", "Apellido", "Documento", "Correo"]; // Tus valores estáticos
  //const allOptions = ["Opción 1", "Opción 2", "Opción 3"]; // Tus opciones para el select

  const tableRows = staticValues.map((value) => {
    // Filtrar las opciones que ya han sido seleccionadas en otras filas
    const availableOptions = columData.filter(
      (option) =>
        !Object.values(selectedValues).includes(option) || selectedValues[value] === option
    );

    return {
      staticValue: value,
      select: (
        <Select
          value={selectedValues[value] || ""}
          onChange={(event) => handleChange(event, value)}
          displayEmpty
          required
        >
          <MenuItem value={""}>Seleccione...</MenuItem>
          {availableOptions.map((option, i) => (
            <MenuItem key={i} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      ),
      // Tus otros valores de fila aquí...
    };
  });

  // Obtiene el valor de un campo específico del conjunto de datos de la tabla nativa de la base de datos
  const getFieldValueNewDB = (fieldName) => {
    return columTableNative[fieldName];
  };

  // Actualiza los datos de la tabla nativa de la base de datos con el nuevo valor del campo especificado.
  const handleFieldChangeNewDB = (fieldName, value) => {
    setColumTableNative({
      ...columTableNative,
      [fieldName]: value,
    });
  };

  // Maneja el establecimiento de la conexión con la base de datos.
  const handleEstablishConnection = async () => {
    setLoading(true);
    const conexionDB = {
      port: newData["port"],
      nameDB: newData["nameDB"],
      nameTable: newData["nameTable"],
      db_username: newData["db_username"],
      db_password: newData["db_password"],
      db_host: newData["db_host"],
    };
    // Verificar si el valor de la clave está vacío y eliminarla si es así
    // for (var clave in newData) {
    //   if (newData.hasOwnProperty(clave) && newData[clave] === "") {
    //     delete newData[clave];
    //   }
    // }
    await requestSourceNewConexionDB(newData);
    setLoading(false);
  };

  // Validaciones --->

  // Estado de las validaciones
  const [isFieldValid, setIsFieldValid] = useState({
    source_name: true,
    source_description: true,
    name: true,
    perfil: true,
    created: true,
    expires_in: true,
    CSV: true,
    EXCEL: true,
    JSON: true,
    db_host: true,
    port: true,
    db_username: true,
    nameDB: true,
    nameTable: true,
    strip_image: true, // Validación para la imagen strip
    location: true,
    address: true,
  });

  // Setea los valores de las validaciones
  const resetFieldValid = () => {
    setIsFieldValid({
      source_name: true,
      source_description: true,
      name: true,
      perfil: true,
      created: true,
      expires_in: true,
      CSV: true,
      EXCEL: true,
      JSON: true,
      db_host: true,
      port: true,
      db_username: true,
      nameDB: true,
      nameTable: true,
      check_in_time: true,
      check_in_minute: true,
      check_out_time: true,
      check_out_minute: true,
      strip_image: true, // Resetear validación de imagen strip
      location: true,
      address: true,
    });
  };

  // Expresiones regulares para diferentes tipos de campos
  const validationPatterns = {
    name: /^[\s\S]*\S[\s\S]*$/, // Al menos un carácter que no sea un espacio
    source_name: /^[\s\S]*\S[\s\S]*$/, // Al menos un carácter que no sea un espacio
    source_description: /^[\s\S]*$/, // Campo opcional: permite strings vacíos o con contenido
    perfil: /^[\s\S]*\S[\s\S]*$/, // Al menos un carácter que no sea un espacio
    created: /^\d{4}-\d{2}-\d{2}$/, // Formato aaaa-mm-dd
    expires_in: /^\d{4}-\d{2}-\d{2}$/, // Formato aaaa-mm-dd
    CSV: /\.csv$/, // Archivos con extension .csv
    EXCEL: /\.(xlsx|xls)$/, // Archivos con extension .xlsx y xls
    JSON: /\.json$/, // Archivos con extension .json
    db_host: /^[\s\S]*\S[\s\S]*$/, // Al menos un carácter que no sea un espacio
    port: /^\d*$/, // Verifica que sea un número
    db_username: /^[\s\S]*\S[\s\S]*$/, // Al menos un carácter que no sea un espacio
    nameDB: /^[\s\S]*\S[\s\S]*$/, // Al menos un carácter que no sea un espacio
    nameTable: /^[\s\S]*\S[\s\S]*$/, // Al menos un carácter que no sea un espacio
    check_in_time: /^\d*$/, // Verifica que sea un número
    check_in_minute: /^\d*$/, // Verifica que sea un número
    check_out_time: /^\d*$/, // Verifica que sea un número
    check_out_minute: /^\d*$/, // Verifica que sea un número
    strip_image: /\.png$/, // Archivos con extensión .png
    // Permitir letras (incluye acentos), números, espacios y signos comunes en direcciones/lugares.
    // Evitar caracteres peligrosos como '<', '>' o '`' para reducir riesgo de inyección.
    // location: máximo 25 caracteres, address: máximo 100 caracteres.
    location: /^[A-Za-zÀ-ÖØ-öø-ÿ0-9\s,\.\-\/\#()º°:;'"&]{1,25}$/, // seguro para nombres de lugares
    address: /^[A-Za-zÀ-ÖØ-öø-ÿ0-9\s,\.\-\/\#()º°:;'"&]{1,100}$/, // seguro para direcciones
  };

  // Compara el valor del input con la expresion regular requerida
  const validateField = (value, pattern) => {
    return pattern.test(value);
  };

  /**
   * Valida un campo del formulario y actualiza su estado de validez.
   *
   * Realiza validaciones específicas según el tipo de campo:
   * - Para la imagen strip: verifica que sea PNG y no supere 2MB (campo opcional).
   * - Para archivos fuente: valida la extensión según el tipo seleccionado.
   * - Para campos de texto: verifica que cumplan con el patrón definido.
   * - Para campos numéricos: verifica que contengan solo dígitos.
   *
   * @param {string} fieldName - El nombre del campo a validar.
   * @param {*} value - El valor del campo (string, File, number, etc.).
   * @returns {void}
   */
  const handleChangeValid = (fieldName, value) => {
    // No validar contraseña
    if (fieldName === "db_password") {
      return;
    }

    // Campo source_description es opcional - siempre es válido
    if (fieldName === "source_description") {
      setIsFieldValid((prevValidity) => ({
        ...prevValidity,
        source_description: true,
      }));
      return;
    }

    // Validación especial para la imagen strip
    if (fieldName === "strip_image") {
      const isGuestOrVisitor = newData.perfil === "Invitado" || newData.perfil === "Visitante";

      // Si no se selecciona archivo
      if (!value) {
        // Para Invitado/Visitante es obligatorio, para otros es válido
        setIsFieldValid((prevValidity) => ({
          ...prevValidity,
          strip_image: !isGuestOrVisitor,
        }));
        return;
      }

      // Validar que sea PNG y no supere 2MB (2,097,152 bytes)
      const isPNG = value.type === "image/png";
      const isValidSize = value.size <= 2097152;
      const isValidImage = isPNG && isValidSize;

      setIsFieldValid((prevValidity) => ({
        ...prevValidity,
        strip_image: isValidImage,
      }));
      return;
    }

    if (fieldName === "source") {
      fieldName = newData.name; // se reemplaza "source" por el tipo de archivo actual "CSV" || "EXCEL" || "JSON"
      // Verifica si se ha seleccionado algún archivo
      if (!value) {
        setIsFieldValid((prevValidity) => ({
          ...prevValidity,
          [fieldName]: true,
        }));
        return;
      }
      const isFieldValid = validateField(value.name, validationPatterns[fieldName]);
      setIsFieldValid((prevValidity) => ({
        ...prevValidity,
        [fieldName]: isFieldValid,
      }));
      return;
    }

    if (
      fieldName === "check_in_time" ||
      fieldName === "check_out_time" ||
      fieldName === "check_in_minute" ||
      fieldName === "check_out_minute"
    ) {
      const isFieldValid = validateField(value, validationPatterns[fieldName]);
      setIsFieldValid((prevValidity) => ({
        ...prevValidity,
        [fieldName]: isFieldValid,
      }));
      return;
    }

    // Para otros campos, valida el valor ingresado (o seleccionado)
    const isFieldValid = validateField(value.trim(), validationPatterns[fieldName]);

    setIsFieldValid((prevValidity) => ({
      ...prevValidity,
      [fieldName]: isFieldValid,
    }));
  };

  // <-----
  // Función para llamar a handleClickSubmit desde el componente padre
  const callHandleClickSubmit = () => {
    if (filterControllerRef.current) {
      filterControllerRef.current.handleClickSubmit();
    }
  };

  return (
    <>
      <Spinner loading={loading}></Spinner>

      {usePermission("DataSource.byFilter") && (
        <FilterController
          dataTable={dataTable}
          data={dataSources}
          dataFilter={dataFilter}
          setListData={setDataSources}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          setTotalPages={setTotalPages}
          requestData={requestDataSources}
          setError={setError}
          setTotalData={setTotalData}
          setEntriesEnd={setEntriesEnd}
          setEntriesStart={setEntriesStart}
          setLoading={setLoading}
          ref={filterControllerRef}
        ></FilterController>
      )}
      <MDAlert
        isAlert={isAlert}
        setIsAlert={setIsAlert}
        typeAlert={typeAlert}
        message={message}
      ></MDAlert>
      <SourceNew
        open={openNew}
        handleClose={handleCloseNew}
        isFormModified={isFormModified}
        isDisabled={isDisabled}
        isAlert={isAlert}
        errorAlert={errorAlert}
        spinner={spinner}
        getFieldValue={getFieldValueNew}
        sourceTypes={sourceTypes}
        carnetTypes={carnetTypes}
        handleFieldChange={handleFieldChangeNew}
        handleSubmit={handleSubmitNew}
        handleEstablishConnection={handleEstablishConnection}
        columTableNative={columTableNative}
        setColumTableNative={setColumTableNative}
        columData={columData}
        statusConexion={statusConexion}
        tableRows={tableRows}
        loading={loading}
        isFieldValid={isFieldValid}
        handleStripImageChange={handleStripImageChange}
        fieldErrorsNew={fieldErrorsNew}
      />
      <SourceEdit
        open={openEdit}
        handleClose={handleCloseEdit}
        isFormModified={isFormModified}
        isDisabled={isDisabled}
        getFieldValue={getFieldValue}
        handleFieldChange={handleFieldChange}
        handleSubmit={handleSubmitEdit}
        stripImageUrl={stripImageUrl}
        stripImageFile={stripImageFile}
        handleStripImageChange={handleStripImageChange}
        fieldErrors={fieldErrors}
        recoveredData={recoveredData}
      />
      {/* <SourceDelete
        openDelete={openDelete}
        handleCloseDelete={handleCloseDelete}
        infoDelete={infoDelete}
        handleSubmitDelete={handleSubmitDelete}
      ></SourceDelete> */}
      <ListDataSource
        handleOpenNew={handleOpenNew}
        sourceList={dataSources}
        handleOpenEdit={handleOpenEdit}
        handleClickDelete={handleClickDelete}
        isAlertSuccess={isAlertSuccess}
        handleCloseAlertSuccess={handleCloseAlertSuccess}
        infoSuccess={infoSuccess}
        handleOpenDataSourcesBoard={handleOpenDataSourcesFileBoard}
        handleOpenEventForm={handleOpenEventForm}
        loading2={loading2}
        entriesStart={entriesStart}
        entriesEnd={entriesEnd}
        totalData={totalData}
        totalPages={totalPages}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
      ></ListDataSource>
      {openDataSourcesFileBoard && (
        <DataSourceFileController
          openBoard={openDataSourcesFileBoard}
          idDataSource={idDataSource}
          name={nameDataSourceFile}
          onClose={handleCloseDataSourcesFileController}
        ></DataSourceFileController>
      )}
    </>
  );
}
